import { Dispatch, useCallback } from 'react';

import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { useDeleteClipLogo } from '@/features/clips/api/delete-clips-logos';
import { useDeleteClipPeople } from '@/features/clips/api/delete-clips-people';
import { usePatchClipLogo } from '@/features/clips/api/patch-clips-logos';
import { usePatchClipPeople } from '@/features/clips/api/patch-clips-people';
import { usePostClipLogo } from '@/features/clips/api/post-clips-logos';
import { usePostClipPeople } from '@/features/clips/api/post-clips-people';
import { PresenceTypeEnum } from '@/types/presences';
import { EVisibility } from '@/types/types';

import { ItemLayoutType, TimeKeepingRecords } from '../types';
import { buildCompositeKey, parseCompositeKey } from '../utils/composite-key';
import { patchClipCache } from '../utils/patch-clip-cache';

import { LayoutAction } from './use-time-keeping-layout';

type UseTimeKeepingRecordsParams = {
  items: ItemLayoutType[];
  dispatch: Dispatch<LayoutAction>;
};

export function useTimeKeepingRecords({ items, dispatch }: UseTimeKeepingRecordsParams) {
  const { activeClip, setActiveClip, getDateTimeCurrentPresence, playerVideoCurrentTime } =
    useBroadcastScheduleProgram();
  const { mutateAsync: postClipLogo } = usePostClipLogo();
  const { mutateAsync: postClipPeople } = usePostClipPeople();
  const { mutateAsync: patchClipLogo } = usePatchClipLogo();
  const { mutateAsync: patchClipPeople } = usePatchClipPeople();
  const { mutateAsync: deleteClipLogo } = useDeleteClipLogo();
  const { mutateAsync: deleteClipPeople } = useDeleteClipPeople();

  const addRecord = useCallback(
    (compositeId: string, supportPresenceId: string, record: TimeKeepingRecords) => {
      dispatch({ type: 'ADD_RECORD', compositeId, supportPresenceId, record });
    },
    [dispatch]
  );

  const updateRecord = useCallback(
    async (
      compositeId: string,
      supportPresenceId: string,
      recordIndex: number,
      partial: Partial<TimeKeepingRecords>
    ) => {
      const { presenceType, presenceId } = parseCompositeKey(compositeId);

      const currentItem = items.find(
        it => it.presence_type === presenceType && it.presence_id === presenceId
      );
      const currentRecord =
        presenceType === PresenceTypeEnum.PEOPLE
          ? currentItem?.records?.[recordIndex]
          : currentItem?.supports.find(s => s.presenceId === supportPresenceId)?.records[
              recordIndex
            ];

      dispatch({ type: 'UPDATE_RECORD', compositeId, supportPresenceId, recordIndex, partial });

      const mergedRecord: TimeKeepingRecords | undefined = currentRecord
        ? { ...currentRecord, ...partial }
        : undefined;

      if (!mergedRecord || !activeClip?.id) return;

      const baseParameters = {
        bbox: [0, 0, 0, 0] as [number, number, number, number],
        visibility: mergedRecord.visibility,
      };

      try {
        if (mergedRecord.id) {
          // Record already persisted — PATCH to update in place, never duplicate
          if (presenceType === PresenceTypeEnum.LOGOS) {
            await patchClipLogo({
              id: mergedRecord.id,
              start_datetime: new Date(mergedRecord.datetimeStart),
              end_datetime:
                mergedRecord.datetimeEnd !== '0' ? new Date(mergedRecord.datetimeEnd) : null,
              parameters: { ...baseParameters, support_id: supportPresenceId },
              silently: true,
            });
          } else if (presenceType === PresenceTypeEnum.PEOPLE) {
            await patchClipPeople({
              id: mergedRecord.id,
              start_datetime: new Date(mergedRecord.datetimeStart),
              end_datetime:
                mergedRecord.datetimeEnd !== '0' ? new Date(mergedRecord.datetimeEnd) : null,
              parameters: { ...baseParameters },
              silently: true,
            });
          }
          // No cache invalidation for PATCH — the record already exists in cache;
          // only timestamps change, which does not affect whether records are visible.
        } else if (mergedRecord.datetimeEnd !== '0') {
          // New record with a definitive end time — POST to create
          const baseData = {
            clip_id: activeClip.id,
            start_datetime: new Date(mergedRecord.datetimeStart),
            end_datetime: new Date(mergedRecord.datetimeEnd),
            entity_score: 1,
            review_state: 1 as 0 | 1,
            silently: true,
          };

          let createdId: string | undefined;

          if (presenceType === PresenceTypeEnum.LOGOS) {
            const created = await postClipLogo({
              ...baseData,
              logo_id: presenceId,
              parameters: { ...baseParameters, support_id: supportPresenceId },
            });
            createdId = created.id;
            patchClipCache(activeClip.id, clip => ({
              ...clip,
              clip_logos: [...(clip.clip_logos ?? []), created],
            }));
            setActiveClip(prev =>
              prev ? { ...prev, clip_logos: [...(prev.clip_logos ?? []), created] } : prev
            );
          } else if (presenceType === PresenceTypeEnum.PEOPLE) {
            const created = await postClipPeople({
              ...baseData,
              people_id: presenceId,
              parameters: { ...baseParameters },
            });
            createdId = created.id;
            patchClipCache(activeClip.id, clip => ({
              ...clip,
              clip_peoples: [...(clip.clip_peoples ?? []), created],
            }));
            setActiveClip(prev =>
              prev ? { ...prev, clip_peoples: [...(prev.clip_peoples ?? []), created] } : prev
            );
          }

          if (createdId) {
            dispatch({
              type: 'UPDATE_RECORD',
              compositeId,
              supportPresenceId,
              recordIndex,
              partial: { id: createdId },
            });
          }
        }
        // datetimeEnd === '0' and no id: active timer not yet stopped, nothing to persist
      } catch (error) {
        console.error(error);
      }
    },
    [
      items,
      activeClip,
      dispatch,
      postClipLogo,
      postClipPeople,
      patchClipLogo,
      patchClipPeople,
      setActiveClip,
    ]
  );

  const removeRecord = useCallback(
    async (compositeId: string, supportPresenceId: string, recordIndex: number) => {
      const { presenceType } = parseCompositeKey(compositeId);
      const currentItem = items.find(
        it => buildCompositeKey(it.presence_type, it.presence_id) === compositeId
      );

      const recordId =
        presenceType === PresenceTypeEnum.PEOPLE
          ? currentItem?.records?.[recordIndex]?.id
          : currentItem?.supports.find(s => s.presenceId === supportPresenceId)?.records[
              recordIndex
            ]?.id;

      dispatch({ type: 'REMOVE_RECORD', compositeId, supportPresenceId, recordIndex });

      if (recordId && activeClip?.id) {
        try {
          if (presenceType === PresenceTypeEnum.LOGOS) {
            await deleteClipLogo({ id: recordId, silently: true });
            patchClipCache(activeClip.id, clip => ({
              ...clip,
              clip_logos: (clip.clip_logos ?? []).filter(l => l.id !== recordId),
            }));
            setActiveClip(prev =>
              prev
                ? { ...prev, clip_logos: (prev.clip_logos ?? []).filter(l => l.id !== recordId) }
                : prev
            );
          } else if (presenceType === PresenceTypeEnum.PEOPLE) {
            await deleteClipPeople({ id: recordId, silently: true });
            patchClipCache(activeClip.id, clip => ({
              ...clip,
              clip_peoples: (clip.clip_peoples ?? []).filter(p => p.id !== recordId),
            }));
            setActiveClip(prev =>
              prev
                ? {
                    ...prev,
                    clip_peoples: (prev.clip_peoples ?? []).filter(p => p.id !== recordId),
                  }
                : prev
            );
          }
        } catch (error) {
          console.error(error);
        }
      }
    },
    [items, activeClip, dispatch, deleteClipLogo, deleteClipPeople, setActiveClip]
  );

  const resetRecords = useCallback(
    async (compositeId: string, supportPresenceId?: string) => {
      const { presenceType } = parseCompositeKey(compositeId);
      const currentItem = items.find(
        it => buildCompositeKey(it.presence_type, it.presence_id) === compositeId
      );

      const idsToDelete =
        presenceType === PresenceTypeEnum.PEOPLE
          ? (currentItem?.records ?? []).map(r => r.id).filter((id): id is string => !!id)
          : (currentItem?.supports ?? [])
              .filter(s => supportPresenceId === undefined || s.presenceId === supportPresenceId)
              .flatMap(s => s.records)
              .map(r => r.id)
              .filter((id): id is string => !!id);

      dispatch({ type: 'RESET_RECORDS', compositeId, supportPresenceId });

      if (idsToDelete.length > 0 && activeClip?.id) {
        if (presenceType === PresenceTypeEnum.LOGOS) {
          await Promise.all(
            idsToDelete.map(id => deleteClipLogo({ id, silently: true }).catch(console.error))
          );
          patchClipCache(activeClip.id, clip => ({
            ...clip,
            clip_logos: (clip.clip_logos ?? []).filter(l => !idsToDelete.includes(l.id ?? '')),
          }));
          setActiveClip(prev =>
            prev
              ? {
                  ...prev,
                  clip_logos: (prev.clip_logos ?? []).filter(
                    l => !idsToDelete.includes(l.id ?? '')
                  ),
                }
              : prev
          );
        } else if (presenceType === PresenceTypeEnum.PEOPLE) {
          await Promise.all(
            idsToDelete.map(id => deleteClipPeople({ id, silently: true }).catch(console.error))
          );
          patchClipCache(activeClip.id, clip => ({
            ...clip,
            clip_peoples: (clip.clip_peoples ?? []).filter(p => !idsToDelete.includes(p.id ?? '')),
          }));
          setActiveClip(prev =>
            prev
              ? {
                  ...prev,
                  clip_peoples: (prev.clip_peoples ?? []).filter(
                    p => !idsToDelete.includes(p.id ?? '')
                  ),
                }
              : prev
          );
        }
      }
    },
    [items, activeClip, dispatch, deleteClipLogo, deleteClipPeople, setActiveClip]
  );

  const buildNewRecord = useCallback(
    (): TimeKeepingRecords => ({
      visibility: EVisibility.MEDIA,
      datetimeStart: getDateTimeCurrentPresence(playerVideoCurrentTime) ?? new Date(),
      datetimeEnd: '0',
    }),
    [getDateTimeCurrentPresence, playerVideoCurrentTime]
  );

  return { addRecord, updateRecord, removeRecord, resetRecords, buildNewRecord };
}
